"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Api from "./API";
import { secondsToTimeString, timeStringToSeconds } from "./config";
export function useGetEmailTemplate({
  apiKey,
  selectedEmailTemplate,
  setBody,
  dispatch,
  setSubject,
  setTemplateAttachments,
  endpoint
}) {
  useQuery({
    queryKey: [selectedEmailTemplate],
    queryFn: () => __async(this, null, function* () {
      if (!selectedEmailTemplate) return;
      const { res } = yield endpoint || Api.getEmailTemplateFromCalendar(apiKey, Number(selectedEmailTemplate.split(" -")[0]));
      return res;
    }),
    onSuccess: (emailTemplate) => {
      setSubject && setSubject(emailTemplate.subject);
      setTemplateAttachments && setTemplateAttachments(emailTemplate.email_attachments);
      setBody(emailTemplate.body);
    },
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: error.errors.join(", "), state: "error" }
    }),
    enabled: !!selectedEmailTemplate
  });
}
export function useGetEvents({
  apiKey,
  dispatch,
  eventTypeId,
  enabled = true
}) {
  const { data: events, isLoading: loadingEvents } = useQuery({
    queryKey: ["events", eventTypeId],
    queryFn: () => __async(this, null, function* () {
      const { res } = yield Api.getEvents(apiKey, {
        date: /* @__PURE__ */ new Date(),
        override_limit: true,
        event_type_id: eventTypeId
      });
      return res.events;
    }),
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: error.errors.join(", "), state: "error" }
    }),
    enabled
  });
  return { events, loadingEvents };
}
export function useGetIntegrationsData({
  applicationId,
  dispatch
}) {
  const {
    data: integrationsData,
    refetch: refetchIntegrationsData,
    isRefetching: refetchingIntegrationsData,
    isLoading: loadingIntegrationsData
  } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => __async(this, null, function* () {
      if (applicationId) {
        const { res } = yield Api.getIntegrationsData(applicationId);
        return res;
      }
    }),
    onError: (error) => {
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          message: `There was an error getting integrations data, ${error}`,
          state: "error"
        }
      });
    }
  });
  return {
    integrationsData,
    refetchIntegrationsData,
    refetchingIntegrationsData,
    loadingIntegrationsData
  };
}
export function useGetCognologyData({
  applicationId,
  dispatch,
  setRecord,
  setCategories,
  setAttachments,
  enabled = true
}) {
  const { data: cognologyData, isLoading: loadingCognologyData } = useQuery({
    queryKey: ["cognology data"],
    queryFn: () => __async(this, null, function* () {
      if (!applicationId) return;
      const { res } = yield Api.getCognologyData(applicationId);
      return res;
    }),
    onSuccess: (res) => {
      if (res && setRecord && setCategories && setAttachments) {
        res.record.candidate_gender === "Unspecified" ? setRecord(__spreadProps(__spreadValues({}, res.record), { candidate_gender: null })) : setRecord(res.record);
        if (res.record.categories) {
          Object.keys(res.record.categories).forEach((key) => {
            var _a;
            let savedCategoryValue = null;
            const savedCategory = (_a = res.record.categories) == null ? void 0 : _a[key];
            if (savedCategory) {
              savedCategoryValue = [
                savedCategory.name || savedCategory.new_value || "",
                savedCategory.id || 0
              ];
            }
            setCategories((prev) => __spreadProps(__spreadValues({}, prev), { [key]: savedCategoryValue }));
          });
        } else {
          res.categories.forEach((category) => {
            setCategories((prev) => __spreadProps(__spreadValues({}, prev), { [category.id]: null }));
          });
        }
        res.record.assets ? setAttachments(res.record.assets) : res.application_assets.forEach((asset) => {
          setAttachments((prev) => __spreadProps(__spreadValues({}, prev), { [asset.id]: null }));
        });
      }
    },
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: `There was an error getting cognology data, ${error}`, state: "error" }
    }),
    enabled
  });
  return { cognologyData, loadingCognologyData };
}
export function useGetSmsTemplatePlaceholders({
  kind,
  dispatch
}) {
  const { data: placeholders } = useQuery({
    queryKey: ["sms placeholders"],
    queryFn: () => __async(this, null, function* () {
      const { res } = yield Api.getSmsTemplatePlaceholders(kind);
      return res.placeholders;
    }),
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: error.errors.join(", "), state: "error" }
    }),
    initialData: []
  });
  return { placeholders };
}
export function useGetAssignableUsers({ dispatch }) {
  const { data: assignableUsers, isLoading: loadingAssignableUsers } = useQuery({
    queryKey: ["assignable users"],
    queryFn: () => __async(this, null, function* () {
      const { res } = yield Api.getAssignableUsers();
      return res.assignable_users;
    }),
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: {
        message: `There was an error getting assignable users data, ${error}`,
        state: "error"
      }
    })
  });
  return { assignableUsers, loadingAssignableUsers };
}
export function useFormQuestions({
  application,
  selectedFormId,
  dispatch,
  formResponseId
}) {
  const { data: formQuestions, isLoading: loadingFormQuestions } = useQuery({
    queryKey: ["formQuestions", selectedFormId, formResponseId],
    queryFn: () => __async(this, null, function* () {
      if (!application || !selectedFormId) return;
      const { res } = yield Api.getFormQuestions({
        form_id: selectedFormId,
        form_response_id: formResponseId
      });
      return res.fields;
    }),
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: `There was an error getting form questions, ${error}`, state: "error" }
    }),
    enabled: !!application && !!selectedFormId && !!formResponseId
  });
  return { formQuestions, loadingFormQuestions };
}
export function useFormResponse({
  FormResponseId,
  dispatch,
  application
}) {
  return useQuery({
    queryKey: ["formQuestionAndAnswers", FormResponseId],
    queryFn: () => __async(this, null, function* () {
      if (!application) return;
      const { res } = yield Api.showFormResponse(
        application.job.id,
        application.id,
        FormResponseId
      );
      return res.question_and_answers;
    }),
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: `There was an error getting form response, ${error}`, state: "error" }
    }),
    enabled: !!application
  });
}
export function useTimestampData({
  selectedAttachment,
  dispatch,
  listRef,
  application,
  newTimestamp,
  setNewTimestamp
}) {
  var _a;
  const queryClient = useQueryClient();
  const [timestampAdded, setTimestampAdded] = useState(false);
  const isVideoInterview = "watch_interview_link" in selectedAttachment;
  const fetchTimestampsAPI = () => {
    if ((selectedAttachment == null ? void 0 : selectedAttachment.id) && application && !isVideoInterview) {
      return Api.getVideoTimestamps(selectedAttachment.id);
    }
    if (isVideoInterview && application) {
      return Api.getVideoInterviewBookmarks(application.id);
    }
  };
  const { data: timestampData, isLoading: loadingTimestampData } = useQuery({
    queryKey: ["timestamp_data", selectedAttachment],
    queryFn: () => __async(this, null, function* () {
      if (fetchTimestampsAPI) {
        const response = yield fetchTimestampsAPI();
        if (response) {
          const { res } = response;
          if (isVideoInterview && "bookmarks" in res) {
            res.bookmarks = res.bookmarks.map((bookmark) => __spreadProps(__spreadValues({}, bookmark), {
              value: secondsToTimeString(bookmark.value)
            }));
          }
          return res;
        }
      }
    }),
    onSettled: () => {
      var _a2;
      if (timestampAdded) {
        (_a2 = listRef.current) == null ? void 0 : _a2.scrollTo(0, listRef.current.scrollHeight);
        setTimestampAdded(false);
      }
    },
    onError: (error) => dispatch({
      type: "SET_SNACKBAR",
      payload: { message: `There was an error getting timestamp data, ${error}`, state: "error" }
    }),
    enabled: !isVideoInterview && ((_a = selectedAttachment == null ? void 0 : selectedAttachment.attached_content_type) == null ? void 0 : _a.includes("video")) || isVideoInterview
  });
  const updateTimestampApi = () => {
    if (!selectedAttachment || !timestampData || !newTimestamp.id || !application) return;
    if (isVideoInterview) {
      return Api.updateVideoInterviewBookmark(
        application.id,
        newTimestamp.id,
        newTimestamp.name,
        timeStringToSeconds(newTimestamp.value)
      );
    } else {
      return Api.updateVideoTimestamp(
        selectedAttachment.id,
        newTimestamp.id,
        newTimestamp.name,
        newTimestamp.value
      );
    }
  };
  const { mutate: updateTimestamp, isLoading: updatingTimestamp } = useMutation({
    mutationFn: () => __async(this, null, function* () {
      const { res } = yield updateTimestampApi();
      return res;
    }),
    onSuccess: () => __async(this, null, function* () {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: "Timestamp updated successfully", state: "success" }
      });
      setNewTimestamp({ id: null, name: "", value: "" });
      yield queryClient.invalidateQueries(["timestamp_data", selectedAttachment]);
    }),
    onError: (error) => {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: `Error updating task: ${error}`, state: "error" }
      });
    }
  });
  const createTimestampApi = () => {
    if (!selectedAttachment || !timestampData || !application) return;
    if (isVideoInterview) {
      return Api.createVideoInterviewBookmark(
        application.id,
        newTimestamp.name,
        timeStringToSeconds(newTimestamp.value)
      );
    } else {
      return Api.createVideoTimestamp(
        selectedAttachment.id,
        newTimestamp.name,
        newTimestamp.value
      );
    }
  };
  const { mutate: addTimestamp } = useMutation({
    mutationFn: () => __async(this, null, function* () {
      const { res } = yield createTimestampApi();
      return res;
    }),
    onSuccess: () => __async(this, null, function* () {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: "Timestamp added successfully", state: "success" }
      });
      setNewTimestamp(__spreadProps(__spreadValues({}, newTimestamp), { name: "", value: "" }));
      dispatch({ type: "RESET_TIMESTAMP_STATE" });
      yield queryClient.invalidateQueries(["timestamp_data", selectedAttachment]);
      setTimestampAdded(true);
    }),
    onError: (error) => {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: `Error adding timestamp: ${error}`, state: "error" }
      });
    }
  });
  const deleteTimestampApi = (id) => {
    if (!selectedAttachment || !timestampData || !application) return;
    if (isVideoInterview) {
      return Api.deleteVideoInterviewBookmark(application == null ? void 0 : application.id, id);
    } else {
      return Api.deleteVideoTimestamp(selectedAttachment.id, id);
    }
  };
  const { mutate: deleteTimestamp } = useMutation({
    mutationFn: (id) => __async(this, null, function* () {
      deleteTimestampApi(id);
    }),
    onSuccess: () => __async(this, null, function* () {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: "Timestamp deleted successfully", state: "success" }
      });
      yield queryClient.invalidateQueries(["timestamp_data", selectedAttachment]);
    }),
    onError: (error) => {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: `Error deleting timestamp: ${error}`, state: "error" }
      });
    }
  });
  return {
    timestampData,
    loadingTimestampData,
    updateTimestamp,
    updatingTimestamp,
    addTimestamp,
    deleteTimestamp
  };
}
export function useCurrentTime(videoRef, setCurrentTime) {
  useEffect(() => {
    const videoElement = videoRef.current;
    const getCurrentTime = () => {
      if (videoRef.current) {
        setCurrentTime(secondsToTimeString(videoRef.current.currentTime));
      }
    };
    if (videoElement) {
      videoElement.addEventListener("timeupdate", getCurrentTime);
      videoElement.addEventListener("pause", getCurrentTime);
    }
    return () => {
      if (videoElement) {
        videoElement.removeEventListener("timeupdate", getCurrentTime);
        videoElement.removeEventListener("pause", getCurrentTime);
      }
    };
  }, [videoRef]);
}
export const useNotepadUpdater = ({
  application,
  dispatch
}) => {
  const queryClient = useQueryClient();
  const { isLoading, mutate } = useMutation({
    mutationFn: (note) => __async(void 0, null, function* () {
      if (application) {
        const { res } = yield Api.updateApplicationNotepad(
          application.job.id,
          application.id,
          note
        );
        return res;
      }
    }),
    onSuccess: () => {
      dispatch({
        type: "SET_SNACKBAR",
        payload: { message: "Notepad updated successfully", state: "success" }
      });
      dispatch({
        type: "SET_OVERVIEW_TAB",
        payload: { hasUnsavedChanges: false }
      });
      queryClient.invalidateQueries(["application"], { exact: true });
    },
    onError: (error) => {
      dispatch({
        type: "SET_SNACKBAR",
        payload: {
          message: `There was an error in updating application notepad, ${error.error}`,
          state: "error"
        }
      });
    }
  });
  return {
    isLoading,
    mutate
  };
};
